import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Slider,
  IconButton,
  Button,
  Popover,
} from '@mui/material';
import { Close as CloseIcon, RestartAlt as ResetIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useTimezone } from '../../contexts/TimezoneContext';
import { useUnitSystem } from '../../hooks/useUnitSystem';
import { toDisplayTemperature, toDisplaySpeed, toMetricTemperature, toMetricSpeed } from '../../utils/units';
import WindCompass from './WindCompass';

export type WeatherPointValues = {
  temperature: number;
  relative_humidity: number;
  wind_speed: number;
  wind_direction: number;
};

interface WeatherPointEditorProps {
  anchorEl: Element | null | undefined;
  timestamp: string;
  values: WeatherPointValues;
  isOverridden: boolean;
  onApply: (timestamp: string, values: WeatherPointValues) => void;
  onReset: (timestamp: string) => void;
  onClose: () => void;
}

const SliderRow: React.FC<{
  label: string;
  value: number;
  unit: string;
  color: string;
  min: number;
  max: number;
  step: number;
  decimals?: number;
  onChange: (v: number) => void;
}> = ({ label, value, unit, color, min, max, step, decimals = 1, onChange }) => (
  <Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color, fontWeight: 600, fontSize: '0.8rem' }}>
        {value.toFixed(decimals)} {unit}
      </Typography>
    </Box>
    <Slider
      value={value}
      onChange={(_, v) => onChange(v as number)}
      min={min}
      max={max}
      step={step}
      sx={{
        color,
        py: 0.75,
        '& .MuiSlider-thumb': { width: 14, height: 14 },
        '& .MuiSlider-rail': { bgcolor: 'rgba(255,255,255,0.1)' },
      }}
    />
  </Box>
);

const WeatherPointEditor: React.FC<WeatherPointEditorProps> = ({
  anchorEl,
  timestamp,
  values,
  isOverridden,
  onApply,
  onReset,
  onClose,
}) => {
  const { selectedTimezone } = useTimezone();
  const unitSystem = useUnitSystem();
  const temperatureUnit = unitSystem === 'imperial' ? '°F' : '°C';
  const speedUnit = unitSystem === 'imperial' ? 'mph' : 'km/h';

  const [temperature, setTemperature] = useState(toDisplayTemperature(values.temperature, unitSystem));
  const [humidity, setHumidity] = useState(values.relative_humidity);
  const [windSpeed, setWindSpeed] = useState(toDisplaySpeed(values.wind_speed, unitSystem));
  const [windDir, setWindDir] = useState(values.wind_direction);

  useEffect(() => {
    setTemperature(toDisplayTemperature(values.temperature, unitSystem));
    setHumidity(values.relative_humidity);
    setWindSpeed(toDisplaySpeed(values.wind_speed, unitSystem));
    setWindDir(values.wind_direction);
  }, [values, unitSystem]);

  const handleApply = () => {
    onApply(timestamp, {
      temperature: toMetricTemperature(temperature, unitSystem),
      relative_humidity: humidity,
      wind_speed: toMetricSpeed(windSpeed, unitSystem),
      wind_direction: windDir,
    });
    onClose();
  };

  const displayTime = dayjs(timestamp).tz(selectedTimezone).format('ddd D MMM, ha');

  return (
    <Popover
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      PaperProps={{
        sx: {
          bgcolor: '#12151f',
          border: '1px solid rgba(255,107,53,0.35)',
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          p: 2.5,
          width: 480,
        },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: '#ff6b35', fontWeight: 700 }}>
          {displayTime}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {isOverridden && (
            <IconButton size="small" onClick={() => onReset(timestamp)} sx={{ color: 'rgba(255,255,255,0.45)' }} title="Reset to forecast">
              <ResetIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(255,255,255,0.45)' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Body: sliders left, compass right */}
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
        {/* Left — 3 sliders */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <SliderRow
            label="Temperature"
            value={temperature}
            unit={temperatureUnit}
            color="#ff6b35"
            min={unitSystem === 'imperial' ? 14 : -10}
            max={unitSystem === 'imperial' ? 122 : 50}
            step={0.5}
            onChange={setTemperature}
          />
          <SliderRow
            label="Humidity"
            value={humidity}
            unit="% RH"
            color="#64b5f6"
            min={0}
            max={100}
            step={1}
            decimals={0}
            onChange={setHumidity}
          />
          <SliderRow
            label="Wind Speed"
            value={windSpeed}
            unit={speedUnit}
            color="#81c784"
            min={0}
            max={unitSystem === 'imperial' ? 125 : 200}
            step={0.5}
            onChange={setWindSpeed}
          />
        </Box>

        {/* Right — compass */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', fontSize: '0.68rem', letterSpacing: 0.5, mb: 1 }}>
            Wind Direction
          </Typography>
          <WindCompass degrees={windDir} onChange={setWindDir} size={160} />
        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="contained"
          size="small"
          onClick={handleApply}
          sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#ff7f50' }, px: 3 }}
        >
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

export default WeatherPointEditor;
